import { Box, SxProps, Tooltip, TooltipProps } from "@mui/material";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";

interface AccessLevelChipProps {
  /** The access level of the user for this dataset */
  accessLevel: DatasetAccessLevel | null;
  /** Whether preview mode is enabled for this application */
  isPreviewModeEnabled?: boolean;
  /** Whether to show tooltip on hover with access level description */
  showTooltip?: boolean;
  /** Placement of the tooltip relative to the chip */
  tooltipPlacement?: TooltipProps["placement"];
  /** Custom styles to apply to the chip */
  sx?: SxProps;
}

const AccessLevelChip = ({
  accessLevel,
  isPreviewModeEnabled = false,
  showTooltip = false,
  tooltipPlacement = "bottom",
  sx,
}: AccessLevelChipProps) => {
  const getAccessLevelTooltip = (accessLevel: DatasetAccessLevel | null): string => {
    if (accessLevel === DatasetAccessLevel.OWNER) {
      return "Full access to modify, share, and manage this dataset";
    }
    if (accessLevel === DatasetAccessLevel.PREVIEW && isPreviewModeEnabled) {
      return "Read-only access to limited data assets";
    }
    if (accessLevel === DatasetAccessLevel.ANALYST) {
      return "Read-only access to all data assets";
    }
    return "";
  };

  const getChipLabel = (accessLevel: DatasetAccessLevel): string => {
    switch (accessLevel) {
      case DatasetAccessLevel.OWNER:
        return "Owner";
      case DatasetAccessLevel.PREVIEW:
        return "Preview";
      case DatasetAccessLevel.ANALYST:
        return "Analyst";
      default:
        return "";
    }
  };

  const shouldRenderChip = (): boolean => {
    if (accessLevel === DatasetAccessLevel.OWNER) {
      return true;
    }
    if (accessLevel === DatasetAccessLevel.PREVIEW && isPreviewModeEnabled) {
      return true;
    }
    if (accessLevel === DatasetAccessLevel.ANALYST) {
      return true;
    }
    return false;
  };

  if (!shouldRenderChip() || !accessLevel) {
    return null;
  }

  const chipElement = (
    <Box
      data-testid="access-level-chip"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        letterSpacing: "0.04em",
        fontWeight: 600,
        height: "18px",
        paddingInline: 1,
        borderRadius: "99px",
        color: "grey.700",
        backgroundColor: "grey.100",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {getChipLabel(accessLevel)}
    </Box>
  );

  const tooltipText = getAccessLevelTooltip(accessLevel);

  return tooltipText && showTooltip ? (
    <Tooltip arrow enterDelay={300} placement={tooltipPlacement} title={tooltipText}>
      {chipElement}
    </Tooltip>
  ) : (
    chipElement
  );
};

export default AccessLevelChip;
